/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  LoaderCircle,
  TriangleAlert,
  Unlink,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';

import { Text } from '@/shared/ui';

import type { LaunchMaterial } from '../../types';

/*
 * Geometry and type are authored inline, as the prototype authors them: a
 * freshly written arbitrary Tailwind class is not reliably generated in this
 * project, and matching the prototype literally is what closed the gap.
 *
 * The hairlines are rgba over the inverse surface rather than a token: the
 * viewer is the one surface in the app that is always dark, and the
 * --border-* ramp is authored for light backgrounds.
 */
const HAIRLINE = 'rgba(255,255,255,.12)';

/** Providers whose embed is a player rather than a document. */
const VIDEO_PROVIDERS = new Set(['youtube', 'vimeo']);

/** Zoom moves in half steps between fit and 300%, as in the prototype. */
const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.5;

interface MaterialViewerProps {
  material: LaunchMaterial | null;
  onClose: () => void;
  /** Set when showing an archived price-table version. */
  archivedNotice?: string | null;
  onOpenCurrent?: () => void;
}

/** Provider embed URL, in the shape each one needs for an iframe. */
function embedSrc(material: LaunchMaterial): string {
  const url = material.embed_url ?? '';
  if (material.provider === 'youtube') {
    const id = url.match(/(?:v=|youtu\.be\/)([\w-]{6,})/)?.[1];
    return id ? `https://www.youtube.com/embed/${id}` : url;
  }
  if (material.provider === 'vimeo') {
    const id = url.match(/vimeo\.com\/(\d+)/)?.[1];
    return id ? `https://player.vimeo.com/video/${id}` : url;
  }
  return url.replace('/view', '/preview');
}

/** A header control: square, borderless, inverse-on-dark. */
const headerButton = {
  width: 'var(--space-10)',
  height: 'var(--space-10)',
  border: 'none',
  background: 'none',
  color: 'var(--text-inverse)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
} as const;

/**
 * Page navigation and zoom, for a paged document.
 *
 * Pages and zoom are separate controls on purpose: the chevrons walk the
 * document, which is what a reader reaches for first. An earlier build
 * spent the chevrons on zoom and left no way to reach page two at all.
 */
function PdfControls({ page, pages, zoom, onPage, onZoom }: {
  page: number;
  pages: number;
  zoom: number;
  onPage: (next: number) => void;
  onZoom: (next: number) => void;
}) {
  const { t } = useTranslation('features/launches');

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
      {pages > 1 && (
        <>
          <button
            type="button"
            onClick={() => onPage(page - 1)}
            disabled={page <= 1}
            aria-label={t('materials.viewer.prevPage')}
            style={{ ...headerButton, opacity: page <= 1 ? 'var(--launch-opacity-disabled)' : 1 }}
          >
            <ChevronLeft size={20} />
          </button>

          <Text
            as="span"
            style={{
              fontSize: 'var(--text-small)',
              minWidth: 'var(--space-12)',
              textAlign: 'center',
              color: 'var(--text-inverse)',
            }}
          >
            {`${page} / ${pages}`}
          </Text>

          <button
            type="button"
            onClick={() => onPage(page + 1)}
            disabled={page >= pages}
            aria-label={t('materials.viewer.nextPage')}
            style={{ ...headerButton, opacity: page >= pages ? 'var(--launch-opacity-disabled)' : 1 }}
          >
            <ChevronRight size={20} />
          </button>

          <span
            style={{
              width: '1px',
              height: 'var(--space-6)',
              background: 'rgba(255,255,255,.2)',
              margin: '0 var(--space-2)',
            }}
          />
        </>
      )}

      <button
        type="button"
        onClick={() => onZoom(Math.max(ZOOM_MIN, zoom - ZOOM_STEP))}
        aria-label={t('materials.viewer.zoomOut')}
        style={headerButton}
      >
        <ZoomOut size={18} />
      </button>

      <button
        type="button"
        onClick={() => onZoom(ZOOM_MIN)}
        style={{
          height: 'var(--space-8)',
          padding: '0 var(--space-2)',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid rgba(255,255,255,.25)',
          background: 'none',
          color: 'var(--text-inverse)',
          fontSize: 'var(--text-small)',
          cursor: 'pointer',
          minWidth: '56px',
        }}
      >
        {`${Math.round(zoom * 100)}%`}
      </button>

      <button
        type="button"
        onClick={() => onZoom(Math.min(ZOOM_MAX, zoom + ZOOM_STEP))}
        aria-label={t('materials.viewer.zoomIn')}
        style={headerButton}
      >
        <ZoomIn size={18} />
      </button>
    </div>
  );
}

/**
 * What a broken embed shows instead of a blank frame.
 *
 * This is the state the whole viewer exists to never skip: an iframe that
 * fails silently is a white rectangle in front of a client.
 */
function BrokenBody({ material, canManage, onClose }: {
  material: LaunchMaterial;
  canManage: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('features/launches');
  const status = material.embed_status ?? 'removed';

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-8)',
        textAlign: 'center',
      }}
    >
      <span
        style={{
          width: '64px',
          height: '64px',
          borderRadius: 'var(--radius-full)',
          background: 'rgba(255,255,255,.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Unlink size={28} />
      </span>

      <Text as="span" style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)' }}>
        {t(`materials.broken.${status}.title`)}
      </Text>

      <Text
        as="span"
        style={{
          fontSize: 'var(--text-body-sm)',
          opacity: 0.8,
          maxWidth: '420px',
          lineHeight: 'var(--leading-normal)',
        }}
      >
        {t(`materials.broken.${status}.${canManage ? 'manager' : 'broker'}`)}
      </Text>

      <button
        type="button"
        onClick={onClose}
        style={{
          marginTop: 'var(--space-2)',
          height: 'var(--space-10)',
          padding: '0 var(--space-5)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid rgba(255,255,255,.3)',
          background: 'none',
          color: 'var(--text-inverse)',
          fontSize: 'var(--text-body-sm)',
          fontWeight: 'var(--font-semibold)',
          cursor: 'pointer',
        }}
      >
        {t('materials.viewer.back')}
      </button>
    </div>
  );
}

/**
 * Full-screen material viewer.
 *
 * A card over a scrim rather than a bare full-bleed panel: the launch board
 * stays visible at the edges, so opening a floor plan reads as a step
 * inside the board instead of a navigation away from it. It opens over the
 * board rather than downloading, because the file is read standing up, on a
 * phone, on whatever signal the sales stand has.
 */
export function MaterialViewer({
  material,
  onClose,
  archivedNotice,
  onOpenCurrent,
}: MaterialViewerProps) {
  const { t } = useTranslation('features/launches');
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);

  const materialId = material?.material_id ?? null;

  /* A new material starts at page one, fit, and loading — otherwise the
     previous document's page number survives into the next one. */
  useEffect(() => {
    setPage(1);
    setZoom(1);
    setLoading(true);
  }, [materialId]);

  /* A viewer that only answers the mouse is a viewer somebody with a
     keyboard cannot leave. Mirrors the property gallery's lightbox. */
  const handleKey = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (!materialId) return undefined;
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [materialId, handleKey]);

  if (!material) return null;

  const isEmbed = material.source === 'embed';
  const broken = isEmbed && (material.embed_status ?? 'ok') !== 'ok';
  const isVideo = isEmbed && VIDEO_PROVIDERS.has(material.provider ?? '');
  const isImage = !isEmbed && (material.mime_type ?? '').startsWith('image/');
  const isPaged = !isEmbed && !isImage;
  const pages = material.page_count ?? 1;
  const wide = material.page_aspect === 'landscape';
  const providerName = t(`materials.providers.${material.provider ?? 'drive'}`);

  /* Chrome's built-in PDF viewer honours these fragment parameters; its own
     toolbar is hidden because this viewer supplies one. */
  const fileSrc = `${material.view?.url ?? ''}#page=${page}&zoom=${Math.round(zoom * 100)}&toolbar=0&navpanes=0`;

  const stage = {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: zoom > 1 ? 'flex-start' : 'center',
    padding: 'var(--space-6)',
  } as const;

  const plate = {
    width: `${Math.round((wide ? 100 : 70) * zoom)}%`,
    maxWidth: 'none',
    flexShrink: 0,
    aspectRatio: wide ? '1.414 / 1' : '1 / 1.414',
    margin: 'auto',
    borderRadius: 'var(--radius-sm)',
    boxShadow: 'var(--shadow-lg)',
    border: 0,
    background: 'var(--surface-primary)',
  } as const;

  return createPortal(
    <div
      data-testid="material-viewer"
      /* The portal lands on document.body, outside the launch area's
         subtree — and the --launch-* tokens are scoped to `.launch-scope`,
         not :root. Without this class the archived-version banner and the
         disabled-control opacity resolve to nothing at all. */
      className="launch-scope"
      role="dialog"
      aria-modal="true"
      aria-label={material.title}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10050,
        background: 'var(--overlay-scrim-heavy)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-6)',
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '1200px',
          height: '100%',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--surface-inverse)',
          color: 'var(--text-inverse)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-xl)',
          animation: 'modalIn 160ms ease',
        }}
      >
        <div
          style={{
            height: 'var(--space-14)',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: '0 var(--space-3) 0 var(--space-5)',
            borderBottom: `1px solid ${HAIRLINE}`,
          }}
        >
          <Text
            as="span"
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 'var(--text-body)',
              fontWeight: 'var(--font-semibold)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {material.title}
          </Text>

          {!broken && isPaged && (
            <PdfControls
              page={page}
              pages={pages}
              zoom={zoom}
              onPage={(next) => { setPage(Math.min(pages, Math.max(1, next))); setLoading(true); }}
              onZoom={setZoom}
            />
          )}

          <button
            type="button"
            onClick={onClose}
            aria-label={t('materials.viewer.close')}
            style={headerButton}
          >
            <X size={20} />
          </button>
        </div>

        {archivedNotice && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-3) var(--space-5)',
              background: 'var(--launch-price-stale-bg)',
              color: 'var(--launch-price-stale-fg)',
            }}
          >
            <TriangleAlert size={16} />
            <Text
              as="span"
              style={{ flex: 1, fontSize: 'var(--text-body-sm)', fontWeight: 'var(--font-medium)' }}
            >
              {archivedNotice}
            </Text>
            {onOpenCurrent && (
              <button
                type="button"
                onClick={onOpenCurrent}
                style={{
                  height: 'var(--space-8)',
                  padding: '0 var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  background: 'var(--button-primary-bg)',
                  color: 'var(--button-primary-text)',
                  fontSize: 'var(--text-small)',
                  fontWeight: 'var(--font-semibold)',
                  cursor: 'pointer',
                }}
              >
                {t('materials.viewer.openCurrent')}
              </button>
            )}
          </div>
        )}

        {broken && (
          <BrokenBody material={material} canManage={!!onOpenCurrent} onClose={onClose} />
        )}

        {!broken && isPaged && (
          <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
            {pages > 1 && (
              <div
                style={{
                  width: '120px',
                  flexShrink: 0,
                  borderRight: `1px solid ${HAIRLINE}`,
                  padding: 'var(--space-3)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-2)',
                  overflow: 'auto',
                }}
              >
                {Array.from({ length: pages }, (_, index) => index + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => { setPage(n); setLoading(true); }}
                    aria-label={t('materials.viewer.goToPage', { page: n })}
                    aria-current={n === page}
                    style={{
                      aspectRatio: wide ? '1.414 / 1' : '1 / 1.414',
                      borderRadius: 'var(--radius-sm)',
                      border: `2px solid ${n === page ? 'var(--text-inverse)' : 'transparent'}`,
                      background: 'rgba(255,255,255,.1)',
                      color: 'var(--text-inverse)',
                      fontSize: 'var(--text-meta)',
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>
            )}

            <div style={stage}>
              <iframe
                title={material.title}
                src={fileSrc}
                onLoad={() => setLoading(false)}
                style={{ ...plate, display: loading ? 'none' : 'block' }}
              />

              {loading && (
                <div
                  style={{
                    margin: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                  }}
                >
                  <LoaderCircle size={24} className="animate-spin" />
                  <Text as="span" style={{ fontSize: 'var(--text-small)', opacity: 0.8 }}>
                    {pages > 1
                      ? t('materials.viewer.loadingPage', { page, pages })
                      : t('materials.viewer.loadingDocument')}
                  </Text>
                </div>
              )}
            </div>
          </div>
        )}

        {!broken && isImage && (
          <div style={stage}>
            <img
              src={material.view?.url}
              alt={material.title}
              onLoad={() => setLoading(false)}
              style={{
                width: `${Math.round(100 * zoom)}%`,
                margin: 'auto',
                objectFit: 'contain',
                borderRadius: 'var(--radius-sm)',
                boxShadow: 'var(--shadow-lg)',
              }}
            />
          </div>
        )}

        {!broken && isVideo && (
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-6)',
            }}
          >
            <iframe
              title={material.title}
              src={embedSrc(material)}
              allowFullScreen
              style={{
                width: '100%',
                maxWidth: '960px',
                aspectRatio: '16/9',
                border: 0,
                borderRadius: 'var(--radius-md)',
              }}
            />
            <Text
              as="span"
              style={{
                fontSize: 'var(--text-small)',
                opacity: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1-5)',
              }}
            >
              <ExternalLink size={13} />
              {t('materials.viewer.videoNotice', { provider: providerName })}
            </Text>
          </div>
        )}

        {!broken && isEmbed && !isVideo && (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              padding: 'var(--space-4)',
              gap: 'var(--space-3)',
            }}
          >
            <iframe
              title={material.title}
              src={embedSrc(material)}
              allowFullScreen
              style={{
                flex: 1,
                border: 0,
                borderRadius: 'var(--radius-md)',
                background: 'var(--surface-primary)',
              }}
            />
            <Text
              as="span"
              style={{
                fontSize: 'var(--text-small)',
                opacity: 0.8,
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
              }}
            >
              <ExternalLink size={14} />
              {t('materials.viewer.embedNotice')}
            </Text>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
